// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import Signup from './pages/Signup';
import Login from './pages/Login';
import Products from './pages/Products';
import AdminPanel from './pages/AdminPanel';
import api from './api';
import './styles.css';

// --- PROFILE IMAGE COMPONENT ---
const ProfileImage = ({ user }) => {
  if (!user) return null;
  const imageUrl = user.profilePicUrl
    ? user.profilePicUrl
    : 'https://via.placeholder.com/32/ccc/333?text=👤';

  return (
    <div className="profile-container">
      <img
        src={imageUrl}
        alt={user.fullName || user.email || 'Profile'}
        className="profile-img"
      />
    </div>
  );
};
// -----------------------------

function AppRoot() {
  const location = useLocation();

  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [deadline, setDeadline] = useState(null); // ISO string or null
  const [deadlineLoading, setDeadlineLoading] = useState(true);

  // reusable function to load deadline
  const loadDeadline = async () => {
    setDeadlineLoading(true);
    try {
      const res = await api.get('/settings');
      setDeadline(res.data?.votingDeadline || null);
    } catch {
      setDeadline(null);
    } finally {
      setDeadlineLoading(false);
    }
  };

  // Watch for login/logout (userChanged event)
  useEffect(() => {
    function onUserChange() {
      try { setUser(JSON.parse(localStorage.getItem('user'))); } catch { setUser(null); }
      setToken(localStorage.getItem('token'));
      loadDeadline();
    }
    window.addEventListener('userChanged', onUserChange);
    return () => window.removeEventListener('userChanged', onUserChange);
  }, []);

  // fetch deadline on mount + listen for admin changes
  useEffect(() => {
    loadDeadline();
    const onSettingsChanged = () => loadDeadline();
    window.addEventListener('settingsChanged', onSettingsChanged);
    return () => window.removeEventListener('settingsChanged', onSettingsChanged);
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('userChanged'));
    setUser(null);
    setToken(null);
    window.location.href = '/login';
  };

  // nav flags
  const isAdminPath = location.pathname.includes('/admin');
  const isAuthPath = location.pathname.includes('/login') || location.pathname.includes('/signup');
  const isLoggedIn = !!token;

  // deadline text for navbar
  const now = new Date();
  let navbarDeadlineText = '';
  if (deadline) {
    const d = new Date(deadline);
    if (isNaN(d.getTime())) {
      navbarDeadlineText = 'Voting deadline (invalid)';
    } else if (d < now) {
      navbarDeadlineText = `Voting closed on ${d.toLocaleString()}`;
    } else {
      navbarDeadlineText = `Voting open until ${d.toLocaleString()}`;
    }
  }

  return (
    <>
      <nav className="topnav">
        <Link to="/" className="logo">
          <img
            src="https://indraq.com/wp-content/uploads/2025/04/White_and_Orange-scaled.png"
            alt="Company Logo"
            className="brand-logo"
          />
        </Link>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ color: '#ffd17a', fontSize: 13, marginRight: 8 }}>
            {deadlineLoading ? 'Loading deadline...' : navbarDeadlineText}
          </div>

          <div className="topnav-links">
            {isLoggedIn && !isAuthPath && (
              <>
                {isAdminPath && <Link to="/products">Vote</Link>}
                {!isAdminPath && user?.isAdmin && <Link to="/admin">Admin</Link>}
                <ProfileImage user={user} />
                <button onClick={logout} className="logout-btn">Logout</button>
              </>
            )}

            {!isLoggedIn && !isAuthPath && (
              <>
                <Link to="/signup">Signup</Link>
                <Link to="/login">Login</Link>
              </>
            )}

            {isAuthPath && (
              <Link to={location.pathname === '/login' ? '/signup' : '/login'}>
                {location.pathname === '/login' ? 'Signup' : 'Login'}
              </Link>
            )}
          </div>
        </div>
      </nav>

      <Routes>
        {/* 🔑 Change: Home (/) now points to Login, not Products */}
        <Route path="/" element={!token ? <Login /> : <Navigate to="/products" />} />
        <Route path="/signup" element={!token ? <Signup /> : <Navigate to="/products" />} />
        <Route path="/login" element={!token ? <Login /> : <Navigate to="/products" />} />
        <Route path="/products" element={<Products deadline={deadline} />} />
        <Route
          path="/admin"
          element={
            token ? (
              <AdminPanel
                deadline={deadline}
                setDeadline={(d) => {
                  setDeadline(d);
                  window.dispatchEvent(new Event('settingsChanged'));
                }}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoot />
    </BrowserRouter>
  );
}
